<script>
import axios from "axios";
import LoadingView from '@/components/LoadingView.vue';
import { ToggleButton, SelectMultipleField, FormButton } from '@/components/form';
import { mapWritableState, mapState, mapActions } from 'pinia';
import { useCommunityStore } from "@/stores/communities";
import PaginationTable from "@/components/PaginationTable.vue";
import Statuses from "@/enums/Statuses";
import CommunityStatuses from "@/enums/CommunityStatuses";
import { useAuthStore } from "@/stores/auth";
import { GlobalPolicy } from "@/policies/global";
import NotificationSubscription from "@/components/NotificationSubscription.vue";
import NotificationTypes from "@/enums/NotificationTypes";
import Community from "@/models/community";
import { NotificationsController } from "@/controllers/notifications";
import SearchInput from "@/components/form/SearchInput.vue";

export default {
  setup() {
    return {
      GlobalPolicy,
      Statuses,
      CommunityStatuses,
      NotificationTypes
    }
  },
  data() {
    return {
      communities: [],
      notifications: [],
      isLoading: true,
      columns: [
        { name: 'name', label: 'Name', default: true, sort: (item) => item.meta?.info?.name || item.kova?.WebGroupName || '' },
        { name: 'sales_status', label: 'Sales Status', default: true, sort: (item) => item.kova?.Status || '' },
        { name: 'published', label: 'Published', default: true, sort: (item) => item.status || '' },
        { name: 'notifications', label: 'Notifications', default: true, sort: false, allowOverflow: true }
      ]
    }
  },
  methods: {
    ...mapActions(useCommunityStore, ['clear']),
    index() {
      axios.get(`divisions/communities`)
        .then(res => {
          this.communities = res.data.map(community => new Community(community));
          this.indexNotifications();
        })
        .catch(e => {
          throw e;
        });
    },
    indexNotifications() {
      NotificationsController.index()
      .then(response => {
        this.notifications = response;
      })
      .catch(error => {
        console.error(error);
      })
      .finally(() => {
        this.isLoading = false;
      })
    },
    save(item) {
      item.status = item.status != 'active' ? 'active' : 'inactive';
      axios.patch(`divisions/${item.id}`, item)
        .then(res => {
          if (res.data?.id) {
            this.index();
          }
        })
        .catch(e => {
          throw e;
        });
    },
    getNotification(community) {
      return this.notifications.find(notification => notification.notifiable_type === 'division' && notification.notifiable_id === community.id) ?? false
    }
  },
  computed: {
    ...mapState(useAuthStore, ['user']),
    ...mapState(useCommunityStore, ['hasFilters']),
    ...mapWritableState(useCommunityStore, ['query', 'page']),
    filtered() {
      let results = this.communities;
      if (!this.query.status.includes(null)) {
        results = results.filter(item => this.query.status.includes(item.status));
      }

      if (this.query.salesStatus.length > 0) {
        results = results.filter(item => this.query.salesStatus.includes(item.kova.Status));
      }

      if (this.query.search != '') {
        const searchTerm = this.query.search.toLowerCase();
        results = results.filter(item => {
          const name = (item.meta?.info?.name || item.kova?.WebGroupName || '').toLowerCase();

          return name.includes(searchTerm);
        });
      }
      return results;
    },
  },
  created() {
    this.index();
  },
  components: { LoadingView, ToggleButton, SelectMultipleField, FormButton, PaginationTable, NotificationSubscription, SearchInput },
}
</script>

<template>
  <div class="flex flex-col h-full w-full">
    <LoadingView v-if="isLoading" />
    <header class="flex items-center justify-between gap-4 flex-shrink-0 w-full">
      <h1 class="flex-0 text-3xl font-medium leading-9 font-headline antialiased  bg-gradient-to-r from-bhav-cool-gray-900 to-bhav-cool-gray-600 text-transparent bg-clip-text inline-block">
        Communities
      </h1>
    </header>
    <div class="flex flex-col mt-4 p-4 rounded-lg">
      <div class="inline-flex flex-col flex-wrap md:flex-row md:justify-start gap-6">
        <div class="mb-4 flex-initial">
          <label for="search" class="block text-sm font-medium leading-5 pb-1.5 text-blue-500">Search by Keyword</label>
          <SearchInput v-model="query.search" color="gray" placeholder="Search..." />
        </div>
        <div class="mb-4 flex-initial">
          <SelectMultipleField v-model="query.status" label="Published" :options="Statuses.statuses" all="All Statuses" />
        </div>
        <div class="mb-4 flex-initial">
          <SelectMultipleField v-model="query.salesStatus" label="Sales Status" :options="CommunityStatuses.statuses" all="All Sales Statuses" empty="All Sales Statuses" />
        </div>
        <div v-if="hasFilters" class="mb-4 flex-initial sm:mt-5">
          <FormButton @click="clear" class="bg-bhav-blue-500 text-white">Clear Filters</FormButton>
        </div>
      </div>
      <PaginationTable
        empty-message="There are currently no community items."
        v-if="!isLoading"
        :legacy="false"
        :columns="columns"
        view="communities-list"
        :data="filtered">
        <template #col-name="{ row }">
          <router-link class="block py-4 whitespace-nowrap"
            :to="{ name: 'community-show', params: { id: row.id } }">
            <div class="flex items-center">
              <div
                class="flex-shrink-0 rounded-full overflow-hidden bg-bhav-blue-400 h-10 w-10 flex items-center justify-center text-white">
                <img v-if="row.image" class="aspect-square object-cover" :src="row.image" alt="">
                <i v-else class="fak fa-community text-xl"></i>
              </div>
              <div class="ml-4">
                <div class="text-sm leading-5 font-medium text-bhav-blue-500">
                  {{ row.meta?.info?.name || row.kova?.WebGroupName }}
                  <span v-if="row?.status == 'new'"
                    class="bg-bhav-cool-gray-50 text-bhav-blue-500 px-2 rounded-full inline-block text-xs leading-5 ml-1">{{ Statuses.label('new') }}</span>
                </div>
              </div>
            </div>
          </router-link>
        </template>
        <template #col-sales_status="{ row }">
          <router-link class="block py-4 whitespace-nowrap"
            :to="{ name: 'community-show', params: { id: row.id } }">
            <span class="px-3 h-8 items-center justify-center inline-flex text-xs leading-5 font-semibold rounded-3xl"
              :class="['Closed'].includes(row.kova?.Status) ? 'bg-bhav-red-100 text-bhav-red-500' : 'bg-bhav-green-100 text-bhav-green-600'">
              {{ row.kova?.Status }}
            </span>
          </router-link>
        </template>
        <template #col-published="{ row }">
          <div class="flex items-center gap-2">
            <ToggleButton v-if="user.level >= 2" :id="`item-${row.id}-status`" @changed="save(row)"
              :checked="row.status == 'active'"
              on-color="green"
              :label="(row.status == 'active' && 'Published') || (row.status == 'unpublished' && 'Unpublished') || row.status == 'new' && Statuses.label('new')" />
            <template v-else>
              <p title="Published" class="text-bhav-blue fas fa-square-check text-xl" v-if="row.status == 'active'"></p>
              <p title="Unpublished" class="text-oakwood-gray-400 fas fa-square-x text-xl" v-else></p>
            </template>
          </div>
        </template>
        <template #col-notifications="{ row }">
          <NotificationSubscription :type="NotificationTypes.DIVISION" :id="row.id" :context="row" :source="getNotification(row)" />
        </template>
      </PaginationTable>
    </div>
  </div>
</template>